
    import * as THREE from 'three';
    import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
    import { TransformControls } from 'three/addons/controls/TransformControls.js';
    import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';
    
    const urlParams = new URLSearchParams(window.location.search);
    const splatUrl = urlParams.get('splat') || '3DGS/Interior_Demo.ksplat';
    
    // Derive config URL from splat URL (replace .ply with .json)
    const configUrl = splatUrl.replace(/\.(ply|splat|ksplat)$/i, '.json');
    
    // Default config
    let config = {
      mode: 'walkthrough',  // 'walkthrough' or 'turntable'
      background: {
        color: '#808080'
      },
      camera: {
        position: [0, 2, 5],
        lookAt: [0, 0, 0],
        fov: 60
      },
      pointcloud: {
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: 1
      },
      turntable: {
        autoRotate: true,
        autoRotateSpeed: 0.5,
        minDistance: 2,
        maxDistance: 10,
        startDistance: 5,
        targetY: 0,
        minPolarAngle: 0,
        maxPolarAngle: Math.PI,
        minAzimuthAngle: -Infinity,
        maxAzimuthAngle: Infinity,
        enablePan: true,
        enableZoom: true
      },
      navigation: {
        floorHeight: 0,
        navmeshHeight: 0,
        eyeHeightStanding: 1.6,
        eyeHeightCrouching: 0.5,
        moveSpeed: 5,
        lookSpeed: 0.002,
        maxWalkDistance: 20,
        showNavmesh: true,
        navmeshSize: 50,
        navmeshOpacity: 0.3,
        navmeshColor: '#888888',
        navmeshPolygon: null,
        hoverIndicatorHeight: 0.5
      },
      ui: {
        showCrouchButton: true,
        showInfoPanel: true,
        showBackButton: true
      }
    };
    
    // Admin state
    let isAdmin = false;
    let isEditingNavmesh = false;
    let navmeshPoints = [];
    
    // Adaptive Performance System
    const performanceMonitor = {
      fps: 60,
      frameCount: 0,
      lastTime: performance.now(),
      targetFps: 30,
      minFps: 20,
      currentQualityLevel: 1.0, // 1.0 = full quality, 0.0 = minimum
      adaptiveEnabled: true,
      
      update() {
        this.frameCount++;
        const now = performance.now();
        const elapsed = now - this.lastTime;
        
        if (elapsed >= 1000) {
          this.fps = Math.round((this.frameCount * 1000) / elapsed);
          this.frameCount = 0;
          this.lastTime = now;
          
          if (this.adaptiveEnabled) {
            this.adjustQuality();
          }
        }
      },
      
      adjustQuality() {
        if (this.fps < this.minFps && this.currentQualityLevel > 0.3) {
          // FPS too low, reduce quality
          this.currentQualityLevel = Math.max(0.3, this.currentQualityLevel - 0.1);
          this.applyQuality();
        } else if (this.fps > this.targetFps + 10 && this.currentQualityLevel < 1.0) {
          // FPS good, slowly increase quality
          this.currentQualityLevel = Math.min(1.0, this.currentQualityLevel + 0.05);
          this.applyQuality();
        }
      },
      
      applyQuality() {
        // DISABLED: setPixelRatio causes black flickers
        // The gaussian splats library doesn't handle dynamic pixel ratio changes well
        // console.log(`Adaptive quality: ${Math.round(this.currentQualityLevel * 100)}%, FPS: ${this.fps}`);
      },
      
      getFps() {
        return this.fps;
      }
    };
    
    let camera;
    let moveSpeed = 5;
    let lookSpeed = 0.002;
    let floorY = 0;
    let isCrouching = false;
    let eyeHeightStanding = 1.6;
    let eyeHeightCrouching = 0.5;
    
    // Grid LOD Manager for sector-based loading
    let gridLODManager = null;
    
        
    // WASD state (q/e for height in edit mode)
    const keys = { w: false, a: false, s: false, d: false, c: false, q: false, e: false };
    
    // Mouse look state
    let isLooking = false;
    let yaw = 0;
    let pitch = 0;
    let sceneReady = false; // Block camera movement until scene is ready
    
    // Click-to-walk state
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -floorY);
    const intersectPoint = new THREE.Vector3();
    let isMoving = false;
    let moveStart = new THREE.Vector3();
    let moveEnd = new THREE.Vector3();
    let moveProgress = 0;
    
    // UI elements
    const loadingOverlay = document.getElementById('loading-overlay');
    const loadingText = document.getElementById('loading-text');
    const loadingProgress = document.getElementById('loading-progress');
    const loadingPercentage = document.getElementById('loading-percentage');
    const clickPrompt = document.getElementById('click-prompt');
    const moveIndicator = document.getElementById('move-indicator');
    
    // Navmesh plane (visible floor for click-to-walk)
    let navMesh;
    let viewer;
    
    // Detect mobile device (defined early for use in init)
    function isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    }
    
    // Load config first, then initialize viewer
    async function init() {
      // Try to load config
      try {
        const response = await fetch(configUrl);
        if (response.ok) {
          const loadedConfig = await response.json();
          // Merge with defaults
          config = { ...config, ...loadedConfig };
          if (loadedConfig.background) config.background = { ...config.background, ...loadedConfig.background };
          if (loadedConfig.camera) config.camera = { ...config.camera, ...loadedConfig.camera };
          if (loadedConfig.pointcloud) config.pointcloud = { ...config.pointcloud, ...loadedConfig.pointcloud };
          if (loadedConfig.turntable) config.turntable = { ...config.turntable, ...loadedConfig.turntable };
          if (loadedConfig.navigation) config.navigation = { ...config.navigation, ...loadedConfig.navigation };
          if (loadedConfig.ui) config.ui = { ...config.ui, ...loadedConfig.ui };
          console.log('Config loaded:', config);
        }
      } catch (e) {
        console.log('No config found, using defaults');
      }
      
      // Apply config values
      moveSpeed = config.navigation.moveSpeed;
      lookSpeed = config.navigation.lookSpeed;
      floorY = config.navigation.floorHeight;
      eyeHeightStanding = config.navigation.eyeHeightStanding || 1.6;
      eyeHeightCrouching = config.navigation.eyeHeightCrouching || 0.5;
      
      // Load navmesh polygon if exists
      if (config.navigation.navmeshPolygon) {
        navmeshPoints = config.navigation.navmeshPolygon;
      }
      
      // Create Three.js scene for navmesh (will integrate with depth buffer)
      const threeScene = new THREE.Scene();
      
      // Set background color from config
      const bgColor = new THREE.Color(config.background.color);
      threeScene.background = bgColor;
      
      // Create viewer with config and integrated Three.js scene
      // Standard Y-up orientation
      const cameraUp = [0, 1, 0];
      
      // Check HQ mode from localStorage
      const savedHQMode = localStorage.getItem('hqMode');
      const isMobile = isMobileDevice();
      // Default: LQ for everyone (fast loading), user can enable HQ
      const isHQ = savedHQMode === 'true';
      
      // Quality settings based on HQ mode
      // HQ mode: full quality, Non-HQ: reduced for performance
      const quality = config.quality || {};
      const hqSettings = {
        antialiased: true,
        sphericalHarmonicsDegree: 2,
        focalAdjustment: 1.0,
        halfPrecision: false,
        compressionLevel: 0
      };
      const lqSettings = {
        antialiased: false,
        sphericalHarmonicsDegree: 0,
        focalAdjustment: 0.6,
        halfPrecision: true,
        compressionLevel: 2
      };
      const activeQuality = isHQ ? hqSettings : lqSettings;
      
      // Limit pixel ratio on mobile for better performance
      // LQ mode: lower pixel ratio for smoother performance
      const maxPixelRatio = isHQ ? (isMobile ? 1.5 : 2) : (isMobile ? 1.0 : 1.5);
      const pixelRatio = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
      
      // Calculate lookAt from rotation if specified (in degrees)
      let cameraLookAt = config.camera.lookAt || [0, 0, 0];
      if (config.camera.rotation !== undefined) {
        // rotation is Y-axis rotation in degrees (0 = +Z, 90 = +X, 180 = -Z, 270 = -X)
        const rotY = config.camera.rotation * Math.PI / 180;
        const distance = 10; // Look 10 units ahead
        const pos = config.camera.position;
        cameraLookAt = [
          pos[0] + Math.sin(rotY) * distance,
          pos[1], // Same height
          pos[2] + Math.cos(rotY) * distance
        ];
        console.log('Camera rotation:', config.camera.rotation, 'degrees, lookAt:', cameraLookAt);
      }
      
      viewer = new GaussianSplats3D.Viewer({
        cameraUp: cameraUp,
        initialCameraPosition: config.camera.position,
        initialCameraLookAt: cameraLookAt,
        useBuiltInControls: false,
        threeScene: threeScene,
        sceneRevealMode: GaussianSplats3D.SceneRevealMode.Instant,
        devicePixelRatio: pixelRatio,
        sharedMemoryForWorkers: true,
        // Quality settings based on HQ mode
        antialiased: quality.antialiased !== undefined ? quality.antialiased : activeQuality.antialiased,
        sphericalHarmonicsDegree: quality.sphericalHarmonicsDegree !== undefined ? quality.sphericalHarmonicsDegree : activeQuality.sphericalHarmonicsDegree,
        focalAdjustment: quality.focalAdjustment !== undefined ? quality.focalAdjustment : activeQuality.focalAdjustment,
        halfPrecisionCovariancesOnGPU: quality.halfPrecision !== undefined ? quality.halfPrecision : activeQuality.halfPrecision,
        inMemoryCompressionLevel: quality.compressionLevel !== undefined ? quality.compressionLevel : activeQuality.compressionLevel
      });
      
      // Set renderer clear color to match background
      viewer.renderer.setClearColor(bgColor, 1);
      
      // Load splat with progressive loading (shows while loading)
      // splatAlphaRemovalThreshold removes splats with low alpha (helps remove background)
      // LQ mode uses higher threshold to remove more splats for better performance
      const baseAlphaThreshold = config.pointcloud.alphaRemovalThreshold !== undefined 
        ? config.pointcloud.alphaRemovalThreshold 
        : 5;
      const alphaThreshold = isHQ ? baseAlphaThreshold : Math.max(baseAlphaThreshold, 15);
      // Convert rotation from Euler degrees to quaternion
      // rotation config is [x, y, z] in degrees
      const rotationDeg = config.pointcloud.rotation || [0, 0, 0];
      const euler = new THREE.Euler(
        rotationDeg[0] * Math.PI / 180,
        rotationDeg[1] * Math.PI / 180,
        rotationDeg[2] * Math.PI / 180
      );
      const quaternion = new THREE.Quaternion().setFromEuler(euler);
      const rotationQuat = [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
      
      const scale = config.pointcloud.scale || 1;
      
      console.log('Splat transform:', {
        position: config.pointcloud.position,
        rotationDeg: rotationDeg,
        rotationQuat: rotationQuat,
        scale: [scale, scale, scale]
      });
      
      // Check if file is .splat (doesn't support progressive loading)
      const isSplatFile = splatUrl.toLowerCase().endsWith('.splat');
      
      // If grid LOD is enabled, skip loading main splat file - sectors will be loaded dynamically
      const useGridLOD = config.grid && config.grid.enabled === true;
      
      if (useGridLOD) {
        console.log('Grid LOD enabled - skipping main splat file, sectors will load dynamically');
        // Start viewer without main splat
        viewer.start();
        camera = viewer.camera;
        
        // Initialize Grid LOD Manager immediately
        initGridLODManager();
        
        // Hide loading overlay
        setTimeout(() => {
          loadingOverlay.classList.add('hidden');
          sceneReady = true;
          console.log('Grid LOD ready - controls enabled');
        }, 500);
        
        // Load hotspots from config
        if (config.hotspots && Array.isArray(config.hotspots)) {
          hotspots = config.hotspots.map(h => ({
            position: new THREE.Vector3(h.position[0], h.position[1], h.position[2]),
            title: h.title,
            description: h.description,
            scale: h.scale || 1.0
          }));
          console.log('Loaded hotspots from config:', hotspots.length);
        }
        
        // Mode-specific setup
        if (config.mode === 'turntable') {
          setupTurntableControls();
          create3DHotspots();
          setupTransformControls();
        } else {
          createNavMesh();
          create3DHotspots();
          setupTransformControls();
          createHoverIndicator();
          updateCameraHeight();
          navmeshCenterX = config.camera.position[0];
          navmeshCenterZ = config.camera.position[2];
          setupControls();
          if (config.camera.rotation !== undefined) {
            yaw = -config.camera.rotation * Math.PI / 180;
          }
        }
        
        applyUISettings();
        clickPrompt.style.display = 'none';
        animate();
        return; // Skip normal splat loading
      }
      
      viewer.addSplatScene(splatUrl, {
        splatAlphaRemovalThreshold: alphaThreshold,
        position: config.pointcloud.position,
        rotation: rotationQuat,
        scale: [scale, scale, scale],
        progressiveLoad: !isSplatFile,  // Disable progressive loading for .splat files
        showLoadingUI: false,    // Disable built-in loading UI
        onProgress: (percent) => {
          const safePercent = (typeof percent === 'number' && !isNaN(percent)) ? percent : 0;
          const roundedPercent = Math.round(safePercent);
          if (roundedPercent < 100) {
            loadingText.textContent = 'Scene verbeteren...';
          } else {
            loadingText.textContent = 'Scene optimaliseren...';
          }
          
          // Only show percentage and progress bar if we have valid progress
          if (safePercent > 0) {
            loadingProgress.style.width = `${safePercent}%`;
            loadingPercentage.textContent = `${roundedPercent}%`;
            loadingPercentage.style.display = 'block';
            document.getElementById('loading-bar').style.display = 'block';
          } else {
            // Hide percentage and progress bar if no valid progress
            loadingPercentage.style.display = 'none';
            document.getElementById('loading-bar').style.display = 'none';
          }
          
          // Hide loading overlay when complete
          if (roundedPercent >= 100) {
            setTimeout(() => {
              loadingOverlay.classList.add('hidden');
            }, 500);
          }
        }
      })
      .then(() => {
        // Start viewer first, then wait for sort to stabilize
        viewer.start();
        camera = viewer.camera;
        
        // Force initial sort by slightly moving camera and back
        // This triggers the sorting algorithm to run
        setTimeout(() => {
          if (camera) {
            const originalPos = camera.position.clone();
            camera.position.x += 0.001;
            viewer.update();
            camera.position.copy(originalPos);
            viewer.update();
          }
        }, 100);
        
        // Simple delay then show scene
        setTimeout(() => {
          loadingOverlay.classList.add('hidden');
          sceneReady = true;
          console.log('Scene ready - controls enabled');
        }, 500);
        
        // Load hotspots from config BEFORE creating them
        if (config.hotspots && Array.isArray(config.hotspots)) {
          hotspots = config.hotspots.map(h => ({
            position: new THREE.Vector3(h.position[0], h.position[1], h.position[2]),
            title: h.title,
            description: h.description,
            scale: h.scale || 1.0
          }));
          console.log('Loaded hotspots from config:', hotspots.length);
          
          // Render hotspot list in admin panel after loading (delayed to ensure function exists)
          setTimeout(() => {
            if (typeof renderHotspotList === 'function') {
              renderHotspotList();
            }
          }, 100);
        }
        
        // Mode-specific setup
        if (config.mode === 'turntable') {
          setupTurntableControls();
          // Create 3D hotspots for turntable mode
          create3DHotspots();
          // Setup transform controls for admin hotspot editing
          setupTransformControls();
        } else {
          // Walkthrough mode
          // Create navmesh plane (needed for click-to-walk and hover indicator)
          createNavMesh();
          
          // Create 3D hotspots for walkthrough mode
          create3DHotspots();
          // Setup transform controls for admin hotspot editing
          setupTransformControls();
          
          // Create hover indicator for click-to-walk
          createHoverIndicator();
          
          // Set initial camera height based on floor + eye height
          updateCameraHeight();
          
          // Set navmesh center to camera start position
          navmeshCenterX = config.camera.position[0];
          navmeshCenterZ = config.camera.position[2];
          
          setupControls();
          
          // Apply camera rotation after controls setup (walkthrough mode)
          if (config.camera.rotation !== undefined) {
            // Set yaw to match config rotation (convert degrees to radians, negate for correct direction)
            yaw = -config.camera.rotation * Math.PI / 180;
            console.log('Applied camera yaw:', yaw, 'radians from', config.camera.rotation, 'degrees');
          }
        }
        
        // Apply UI visibility settings
        applyUISettings();
        
        // Initialize Grid LOD Manager if enabled
        if (config.grid && config.grid.enabled) {
          initGridLODManager();
        }
        
        clickPrompt.style.display = 'none';
        
        animate();
      })
      .catch((error) => {
        loadingText.textContent = 'Fout bij laden: ' + error.message;
        console.error(error);
      });
    }
    
    init();
    
    function createNavMesh() {
      const navOpacity = config.navigation.navmeshOpacity;
      const navColor = new THREE.Color(config.navigation.navmeshColor || '#888888');
      
      // Check if we have a custom polygon
      if (config.navigation.navmeshPolygon && config.navigation.navmeshPolygon.length >= 3) {
        // Create mesh from polygon points
        // Points are [x, z] in world space, Shape uses [x, y] in 2D
        const shape = new THREE.Shape();
        const points = config.navigation.navmeshPolygon;
        
        // Shape is in XY plane, we rotate it to XZ plane
        // So Shape X = World X, Shape Y = World Z
        shape.moveTo(points[0][0], points[0][1]);
        for (let i = 1; i < points.length; i++) {
          shape.lineTo(points[i][0], points[i][1]);
        }
        shape.closePath();
        
        const geometry = new THREE.ShapeGeometry(shape);
        const material = new THREE.MeshBasicMaterial({
          color: navColor,
          transparent: true,
          opacity: navOpacity,
          side: THREE.DoubleSide,
          visible: navOpacity > 0
        });
        navMesh = new THREE.Mesh(geometry, material);
        navMesh.rotation.x = -Math.PI / 2; // Rotate from XY to XZ plane (lay flat)
        navMesh.position.y = config.navigation.navmeshHeight;
        
        // Also populate polygonPoints for bounds checking (x, y, z where y is height)
        polygonPoints = points.map(p => ({ x: p[0], z: p[1] }));
        
        // Add visual markers for each point (for debugging)
        if (navOpacity > 0) {
          points.forEach((p, i) => {
            const markerGeom = new THREE.SphereGeometry(0.15);
            const markerMat = new THREE.MeshBasicMaterial({ color: i === 0 ? 0x00ff00 : 0xff0000 });
            const marker = new THREE.Mesh(markerGeom, markerMat);
            marker.position.set(p[0], config.navigation.navmeshHeight + 0.1, p[1]);
            viewer.threeScene.add(marker);
          });
        }
      } else {
        // Fallback to square plane
        const navSize = config.navigation.navmeshSize;
        const geometry = new THREE.PlaneGeometry(navSize, navSize);
        const material = new THREE.MeshBasicMaterial({
          color: navColor,
          transparent: true,
          opacity: navOpacity,
          side: THREE.DoubleSide,
          visible: navOpacity > 0
        });
        navMesh = new THREE.Mesh(geometry, material);
        navMesh.rotation.x = -Math.PI / 2; // Lay flat
        navMesh.position.set(config.camera.position[0], config.navigation.navmeshHeight, config.camera.position[2]);
        
        // Add grid for square navmesh only
        const gridDivisions = navSize;
        const gridHelper = new THREE.GridHelper(navSize, gridDivisions, 0xffffff, 0x666666);
        gridHelper.position.set(config.camera.position[0], config.navigation.navmeshHeight + 0.01, config.camera.position[2]);
        gridHelper.material.transparent = true;
        gridHelper.material.opacity = navOpacity > 0 ? 0.5 : 0;
        gridHelper.material.side = THREE.DoubleSide;
        gridHelper.visible = navOpacity > 0;
        viewer.threeScene.add(gridHelper);
      }
      
      viewer.threeScene.add(navMesh);
    }
    
    // Turntable orbit controls
    let orbitControls = null;
    
    function setupTurntableControls() {
      // Create orbit controls
      orbitControls = new OrbitControls(camera, viewer.renderer.domElement);
      
      // Apply turntable config
      orbitControls.enableDamping = true;
      orbitControls.dampingFactor = 0.05;
      orbitControls.autoRotate = config.turntable.autoRotate;
      orbitControls.autoRotateSpeed = config.turntable.autoRotateSpeed;
      orbitControls.minDistance = config.turntable.minDistance;
      orbitControls.maxDistance = config.turntable.maxDistance;
      orbitControls.enablePan = config.turntable.enablePan;
      orbitControls.enableZoom = config.turntable.enableZoom;
      
      // Helper to convert degrees to radians
      const degToRad = (deg) => deg * Math.PI / 180;
      
      // Polar angle limits (vertical rotation limits) - in degrees
      // 0° = looking from top, 90° = horizontal, 180° = looking from bottom
      const minPolar = config.turntable.minPolarAngle !== undefined ? degToRad(config.turntable.minPolarAngle) : 0;
      const maxPolar = config.turntable.maxPolarAngle !== undefined ? degToRad(config.turntable.maxPolarAngle) : Math.PI;
      orbitControls.minPolarAngle = minPolar;
      orbitControls.maxPolarAngle = maxPolar;
      
      // Azimuth angle limits (horizontal rotation limits) - in degrees
      // Use Infinity for unlimited rotation
      if (config.turntable.minAzimuthAngle !== undefined) {
        orbitControls.minAzimuthAngle = degToRad(config.turntable.minAzimuthAngle);
      }
      if (config.turntable.maxAzimuthAngle !== undefined) {
        orbitControls.maxAzimuthAngle = degToRad(config.turntable.maxAzimuthAngle);
      }
      
      // Set target with configurable Y height
      const targetY = config.turntable.targetY !== undefined ? config.turntable.targetY : config.camera.lookAt[1];
      orbitControls.target.set(
        config.camera.lookAt[0],
        targetY,
        config.camera.lookAt[2]
      );
      
      // Set initial camera distance
      if (config.turntable.startDistance) {
        const direction = new THREE.Vector3().subVectors(camera.position, orbitControls.target).normalize();
        camera.position.copy(orbitControls.target).add(direction.multiplyScalar(config.turntable.startDistance));
      }
      
      orbitControls.update();
      
      // Auto-rotate pause on user interaction
      let autoRotateTimeout = null;
      const autoRotateDelay = 30000; // 30 seconds
      
      const pauseAutoRotate = () => {
        if (config.turntable.autoRotate && orbitControls) {
          orbitControls.autoRotate = false;
          
          // Clear existing timeout
          if (autoRotateTimeout) {
            clearTimeout(autoRotateTimeout);
          }
          
          // Resume after delay
          autoRotateTimeout = setTimeout(() => {
            if (orbitControls) {
              orbitControls.autoRotate = true;
            }
          }, autoRotateDelay);
        }
      };
      
      // Listen for user interaction
      viewer.renderer.domElement.addEventListener('pointerdown', pauseAutoRotate);
      viewer.renderer.domElement.addEventListener('wheel', pauseAutoRotate);
      viewer.renderer.domElement.addEventListener('touchstart', pauseAutoRotate);
      
      // Click handler for turntable mode hotspots
      let turntableMouseDown = { x: 0, y: 0, time: 0 };
      viewer.renderer.domElement.addEventListener('pointerdown', (e) => {
        turntableMouseDown = { x: e.clientX, y: e.clientY, time: Date.now() };
      });
      viewer.renderer.domElement.addEventListener('pointerup', (e) => {
        const deltaX = Math.abs(e.clientX - turntableMouseDown.x);
        const deltaY = Math.abs(e.clientY - turntableMouseDown.y);
        const duration = Date.now() - turntableMouseDown.time;
        
        // Only trigger on short clicks without much movement
        if (deltaX < 5 && deltaY < 5 && duration < 300) {
          check3DHotspotClick(e);
        }
      });
    }
    
    function setupControls() {
      const canvas = viewer.renderer.domElement;
      
      // Track mouse state
      let lastMouseX = 0;
      let lastMouseY = 0;
      let mouseDownTime = 0;
      
      // WASD keyboard controls
      window.addEventListener('keydown', (e) => {
        if (!e.key) return;
        const key = e.key.toLowerCase();
        if (key in keys) keys[key] = true;
        
        // C key toggles crouch
        if (key === 'c' && !e.repeat) {
          toggleCrouch();
        }
      });
      
      window.addEventListener('keyup', (e) => {
        if (!e.key) return;
        const key = e.key.toLowerCase();
        if (key in keys) keys[key] = false;
      });
      
      // Mouse down - start looking (but not when dragging gizmo)
      canvas.addEventListener('mousedown', (e) => {
        if (e.button === 0) {
          // Don't start looking if gizmo is selected (user might be dragging it)
          if (transformControls && transformControls.dragging) return;
          isLooking = true;
          lastMouseX = e.clientX;
          lastMouseY = e.clientY;
          mouseDownTime = Date.now();
          canvas.style.cursor = 'grabbing';
        }
      });
      
      // Mouse up - stop looking, check for click-to-walk or hotspot click
      canvas.addEventListener('mouseup', (e) => {
        if (e.button === 0) {
          const deltaX = Math.abs(e.clientX - lastMouseX);
          const deltaY = Math.abs(e.clientY - lastMouseY);
          const clickDuration = Date.now() - mouseDownTime;
          
          // Only process click if barely moved and short duration
          if (deltaX < 3 && deltaY < 3 && clickDuration < 150) {
            // First check if we clicked a 3D hotspot
            const hotspotClicked = check3DHotspotClick(e);
            
            // If no hotspot was clicked, handle click-to-walk (walkthrough mode only)
            if (!hotspotClicked && config.mode !== 'turntable') {
              handleClickToWalk(e);
            }
          }
          
          isLooking = false;
          canvas.style.cursor = 'grab';
        }
      });
      
      // Mouse move - look around when dragging, update hover indicator
      canvas.addEventListener('mousemove', (e) => {
        if (isLooking && sceneReady) {
          const deltaX = e.clientX - lastMouseX;
          const deltaY = e.clientY - lastMouseY;
          
          yaw -= deltaX * lookSpeed;
          pitch -= deltaY * lookSpeed;
          
          // Limit pitch
          pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, pitch));
          
          lastMouseX = e.clientX;
          lastMouseY = e.clientY;
        }
        
        // Update hover indicator
        updateHoverIndicator(e);
      });
      
      // Set initial cursor
      canvas.style.cursor = 'grab';
      
      // Touch controls
      let touchId = null;
      let lastTouchX = 0, lastTouchY = 0;
      let touchStartX = 0, touchStartY = 0;
      let touchStartTime = 0;
      
      // Pinch-to-zoom (FOV) variables
      let lastPinchDistance = 0;
      let isPinching = false;
      const MIN_FOV = 10;
      const MAX_FOV = 110;
      
      function getPinchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }
      
      canvas.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
          touchId = e.touches[0].identifier;
          touchStartX = lastTouchX = e.touches[0].clientX;
          touchStartY = lastTouchY = e.touches[0].clientY;
          touchStartTime = Date.now();
          isLooking = true;
          isPinching = false;
        } else if (e.touches.length === 2) {
          // Start pinch-to-zoom
          isPinching = true;
          isLooking = false;
          lastPinchDistance = getPinchDistance(e.touches);
        }
      });
      
      canvas.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2 && isPinching && camera) {
          // Pinch-to-zoom: adjust FOV
          const currentDistance = getPinchDistance(e.touches);
          const delta = currentDistance - lastPinchDistance;
          
          // Pinch out (spread fingers) = zoom in = lower FOV
          // Pinch in (pinch fingers) = zoom out = higher FOV
          const fovChange = -delta * 0.1;
          camera.fov = Math.max(MIN_FOV, Math.min(MAX_FOV, camera.fov + fovChange));
          camera.updateProjectionMatrix();
          
          lastPinchDistance = currentDistance;
          e.preventDefault();
          return;
        }
        
        for (let touch of e.touches) {
          if (touch.identifier === touchId && !isPinching) {
            const deltaX = touch.clientX - lastTouchX;
            const deltaY = touch.clientY - lastTouchY;
            
            yaw -= deltaX * lookSpeed;
            pitch -= deltaY * lookSpeed;
            pitch = Math.max(-1.5, Math.min(1.5, pitch));
            
            lastTouchX = touch.clientX;
            lastTouchY = touch.clientY;
          }
        }
        e.preventDefault();
      }, { passive: false });
      
      canvas.addEventListener('touchend', (e) => {
        if (e.changedTouches.length === 1 && !isPinching) {
          const touch = e.changedTouches[0];
          const deltaX = Math.abs(touch.clientX - touchStartX);
          const deltaY = Math.abs(touch.clientY - touchStartY);
          const tapDuration = Date.now() - touchStartTime;
          
          // Only navigate if barely moved (< 5px) and very short tap (< 150ms)
          if (deltaX < 5 && deltaY < 5 && tapDuration < 150) {
            handleClickToWalk({ clientX: touch.clientX, clientY: touch.clientY });
          }
        }
        // Reset pinch state when all fingers lifted
        if (e.touches.length === 0) {
          isPinching = false;
        }
        isLooking = false;
        touchId = null;
      });
    }
    
    function handleClickToWalk(e) {
      // Disable click-to-walk during admin/editor mode
      if (!camera || isAdmin || isEditingNavmesh) return;
      
      // Convert click position to normalized device coordinates
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
      
      raycaster.setFromCamera(mouse, camera);
      
      // Raycast against floor plane (same as hover indicator)
      const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -config.navigation.navmeshHeight);
      const point = new THREE.Vector3();
      
      if (raycaster.ray.intersectPlane(floorPlane, point) && isPositionInNavmesh(point.x, point.z, 0.2)) {
        // Limit distance
        const maxDistance = config.navigation.maxWalkDistance || 20;
        const direction = point.clone().sub(camera.position);
        direction.y = 0; // Keep horizontal
        
        if (direction.length() > maxDistance) {
          direction.normalize().multiplyScalar(maxDistance);
          point.copy(camera.position).add(direction);
        }
        
        moveStart.copy(camera.position);
        // Keep camera at current Y height, only move X and Z
        moveEnd.set(point.x, camera.position.y, point.z);
        moveProgress = 0;
        isMoving = true;
        
      }
    }
    
    // 3D hover indicator for navmesh
    let hoverIndicator = null;
    
    // Separate scene and camera for hover indicator overlay
    let hoverScene = null;
    let hoverCamera = null;
    
    function createHoverIndicator() {
      // Create separate scene for hover indicator (renders after main scene)
      hoverScene = new THREE.Scene();
      
      const geometry = new THREE.RingGeometry(0.3, 0.4, 32);
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide
      });
      hoverIndicator = new THREE.Mesh(geometry, material);
      hoverIndicator.rotation.x = -Math.PI / 2; // Lay flat
      hoverIndicator.visible = false;
      hoverScene.add(hoverIndicator);
    }
    
    function updateHoverIndicator(e) {
      // Disable hover indicator during admin/editor mode
      if (!camera || !hoverIndicator || isLooking || isAdmin || isEditingNavmesh) {
        if (hoverIndicator) hoverIndicator.visible = false;
        return;
      }
      
      const mouse = new THREE.Vector2();
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
      
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      
      // Raycast against navmesh height plane
      const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -config.navigation.navmeshHeight);
      const point = new THREE.Vector3();
      
      if (raycaster.ray.intersectPlane(floorPlane, point)) {
        // Check if point is within navmesh bounds
        if (isPositionInNavmesh(point.x, point.z, 0.2)) {
          // Position at navmesh height (rendered in separate pass so always visible)
          hoverIndicator.position.set(point.x, config.navigation.navmeshHeight, point.z);
          hoverIndicator.visible = true;
        } else {
          hoverIndicator.visible = false;
        }
      } else {
        hoverIndicator.visible = false;
      }
    }
    
    // Navmesh center (set to camera start position)
    let navmeshCenterX = 0;
    let navmeshCenterZ = 0;
    
    // Check if position is within navmesh bounds (with optional margin)
    function isPositionInNavmesh(x, z, margin = 0) {
      // If we have a custom polygon, check against that
      if (polygonPoints.length >= 3) {
        return isPointInPolygonWithMargin(x, z, polygonPoints, margin);
      }
      
      // Check against the square navmesh (centered at camera start position)
      const halfSize = config.navigation.navmeshSize / 2 - margin;
      return x >= navmeshCenterX - halfSize && x <= navmeshCenterX + halfSize && 
             z >= navmeshCenterZ - halfSize && z <= navmeshCenterZ + halfSize;
    }
    
    // Check if point is in polygon with margin (shrink polygon inward)
    function isPointInPolygonWithMargin(x, z, polygon, margin) {
      if (margin <= 0) {
        return isPointInPolygon(x, z, polygon);
      }
      
      // For margin check: point must be inside AND at least margin distance from all edges
      if (!isPointInPolygon(x, z, polygon)) {
        return false;
      }
      
      // Check distance to each edge
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, zi = polygon[i].z;
        const xj = polygon[j].x, zj = polygon[j].z;
        
        // Distance from point to line segment
        const dist = distanceToLineSegment(x, z, xi, zi, xj, zj);
        if (dist < margin) {
          return false;
        }
      }
      return true;
    }
    
    // Calculate distance from point to line segment
    function distanceToLineSegment(px, pz, x1, z1, x2, z2) {
      const dx = x2 - x1;
      const dz = z2 - z1;
      const lengthSq = dx * dx + dz * dz;
      
      if (lengthSq === 0) {
        // Line segment is a point
        return Math.sqrt((px - x1) * (px - x1) + (pz - z1) * (pz - z1));
      }
      
      // Project point onto line, clamped to segment
      let t = ((px - x1) * dx + (pz - z1) * dz) / lengthSq;
      t = Math.max(0, Math.min(1, t));
      
      const nearestX = x1 + t * dx;
      const nearestZ = z1 + t * dz;
      
      return Math.sqrt((px - nearestX) * (px - nearestX) + (pz - nearestZ) * (pz - nearestZ));
    }
    
    // Point in polygon test (ray casting algorithm)
    function isPointInPolygon(x, z, polygon) {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, zi = polygon[i].z;
        const xj = polygon[j].x, zj = polygon[j].z;
        
        if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / (zj - zi) + xi)) {
          inside = !inside;
        }
      }
      return inside;
    }
    
    // Update camera height based on crouch state
    function updateCameraHeight() {
      if (!camera) return;
      const eyeHeight = isCrouching ? eyeHeightCrouching : eyeHeightStanding;
      // Standard Y-up: add eye height to floor
      const targetHeight = floorY + eyeHeight;
      camera.position.y = targetHeight;
    }
    
    // Toggle crouch state
    function toggleCrouch() {
      isCrouching = !isCrouching;
      updateCameraHeight();
      updateCrouchButton();
    }
    
    // Update crouch button visual
    function updateCrouchButton() {
      const btn = document.getElementById('crouch-btn');
      const iconStanding = document.getElementById('crouch-icon-standing');
      const iconCrouching = document.getElementById('crouch-icon-crouching');
      
      if (isCrouching) {
        btn.classList.add('crouching');
        iconStanding.style.display = 'none';
        iconCrouching.style.display = 'block';
      } else {
        btn.classList.remove('crouching');
        iconStanding.style.display = 'block';
        iconCrouching.style.display = 'none';
      }
    }
    
    // Crouch button click handler
    document.getElementById('crouch-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      toggleCrouch();
    });
    
    // HQ mode state
    let isHQMode = false;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    
    // Default: LQ for everyone (fast loading), HQ is opt-in
    isHQMode = localStorage.getItem('hqMode') === 'true';
    
    // HQ button click handler
    document.getElementById('hq-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      toggleHQMode();
    });
    
    // AR button click handler - open AR viewer
    document.getElementById('ar-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      const url = new URL(window.location.href);
      url.pathname = url.pathname.replace('viewer.html', 'viewer-ar.html');
      url.searchParams.set('xr', 'ar');
      window.location.href = url.toString();
    });
    
    function toggleHQMode() {
      isHQMode = !isHQMode;
      updateHQButton();
      
      // Show reload message
      const hqBtn = document.getElementById('hq-btn');
      hqBtn.title = isHQMode ? 'Hoge Kwaliteit (AAN) - Herlaad voor effect' : 'Hoge Kwaliteit (UIT) - Herlaad voor effect';
      
      // Store preference
      localStorage.setItem('hqMode', isHQMode ? 'true' : 'false');
      
      // Reload page to apply new quality settings
      window.location.reload();
    }
    
    function updateHQButton() {
      const hqBtn = document.getElementById('hq-btn');
      if (isHQMode) {
        hqBtn.classList.add('active');
        hqBtn.title = 'Hoge Kwaliteit (AAN)';
      } else {
        hqBtn.classList.remove('active');
        hqBtn.title = 'Hoge Kwaliteit (UIT)';
      }
    }
    
    // Update HQ button state
    updateHQButton();
    
    // Apply UI settings from config
    function applyUISettings() {
      const isTurntable = config.mode === 'turntable';
      const isMobile = isMobileDevice();
      
      // Crouch button visibility (only in walkthrough mode)
      const crouchBtn = document.getElementById('crouch-btn');
      if (crouchBtn) {
        crouchBtn.style.display = (!isTurntable && config.ui.showCrouchButton) ? 'flex' : 'none';
      }
      
      // AR button visibility (only in turntable mode on mobile with WebXR support)
      const arBtn = document.getElementById('ar-btn');
      if (arBtn && isTurntable && isMobile) {
        if (navigator.xr) {
          navigator.xr.isSessionSupported('immersive-ar').then((supported) => {
            arBtn.style.display = supported ? 'flex' : 'none';
          }).catch(() => {
            arBtn.style.display = 'none';
          });
        }
      } else if (arBtn) {
        arBtn.style.display = 'none';
      }
      
      // Info panel visibility - update content based on mode and device
      const infoPanel = document.getElementById('info-panel');
      if (infoPanel) {
        let controlsHtml = '';
        
        if (isTurntable) {
          // Turntable mode controls
          infoPanel.querySelector('h3').textContent = 'Bediening';
          if (isMobile) {
            controlsHtml = `
              <div class="control-item"><span class="key">1 vinger</span> Draaien</div>
              <div class="control-item"><span class="key">Knijpen</span> Zoomen</div>
              ${config.turntable.enablePan ? '<div class="control-item"><span class="key">2 vingers</span> Verschuiven</div>' : ''}
            `;
          } else {
            controlsHtml = `
              <div class="control-item"><span class="key">Slepen</span> Draaien</div>
              <div class="control-item"><span class="key">Scrollen</span> Zoomen</div>
              ${config.turntable.enablePan ? '<div class="control-item"><span class="key">Rechts slepen</span> Verschuiven</div>' : ''}
            `;
          }
        } else {
          // Walkthrough mode controls
          infoPanel.querySelector('h3').textContent = 'Navigatie';
          if (isMobile) {
            controlsHtml = `
              <div class="control-item"><span class="key">1 vinger</span> Rondkijken</div>
              <div class="control-item"><span class="key">Tik vloer</span> Loop naar punt</div>
              <div class="control-item"><span class="key">Knijpen</span> Zoomen</div>
            `;
          } else {
            controlsHtml = `
              <div class="control-item"><span class="key">WASD</span> Bewegen</div>
              <div class="control-item"><span class="key">C</span> Bukken/Opstaan</div>
              <div class="control-item"><span class="key">Slepen</span> Rondkijken</div>
              <div class="control-item"><span class="key">Klik</span> Loop naar punt</div>
            `;
          }
        }
        
        // Find or create controls container
        let controlsContainer = infoPanel.querySelector('.controls-container');
        if (!controlsContainer) {
          controlsContainer = document.createElement('div');
          controlsContainer.className = 'controls-container';
          infoPanel.appendChild(controlsContainer);
        }
        controlsContainer.innerHTML = controlsHtml;
        
        infoPanel.style.display = config.ui.showInfoPanel ? 'block' : 'none';
      }
      
      // Back button visibility
      const backBtn = document.getElementById('back-btn');
      if (backBtn) {
        backBtn.style.display = config.ui.showBackButton ? 'flex' : 'none';
      }
      
      // Admin panel (only in walkthrough mode)
      const adminPanel = document.getElementById('admin-panel');
      if (adminPanel && isTurntable) {
        adminPanel.style.display = 'none';
      }
    }
    
    /**
     * Initialize Grid LOD Manager for sector-based loading
     */
    function initGridLODManager() {
      if (!config.grid || !viewer) return;
      
      // Create GridLODManager instance
      gridLODManager = {
        viewer: viewer,
        camera: camera,
        gridSize: config.grid.cellSize || 10,
        gridOrigin: config.grid.origin || [0, 0],
        basePath: config.grid.basePath || '3DGS/sectors/',
        lodDistances: config.grid.lodDistances || { high: 15, medium: 30, low: 60 },
        sectors: new Map(),
        updateInterval: 500,
        lastUpdateTime: 0,
        maxConcurrentLoads: 1,  // Must be 1 - library can't handle concurrent loads
        currentLoads: 0,
        loadingQueue: [],
        loadDelay: 100,  // ms delay between loads to let library stabilize
        
        initialize() {
          const sectors = config.grid.sectors || [];
          for (const s of sectors) {
            const sectorId = s.id || `${s.col}_${s.row}`;
            // Use center from config if available, otherwise calculate from grid
            const centerX = s.center ? s.center[0] : this.gridOrigin[0] + (s.col + 0.5) * this.gridSize;
            const centerZ = s.center ? s.center[1] : this.gridOrigin[1] + (s.row + 0.5) * this.gridSize;
            
            this.sectors.set(sectorId, {
              id: sectorId,
              col: s.col,
              row: s.row,
              centerX: centerX,
              centerZ: centerZ,
              insideNavmesh: s.insideNavmesh !== false, // Default true
              files: {
                high: s.high || `${this.basePath}${sectorId}_high.ksplat`,
                medium: s.medium || `${this.basePath}${sectorId}_med.ksplat`,
                low: s.low || `${this.basePath}${sectorId}_low.ksplat`
              },
              currentLOD: null,
              targetLOD: null,
              loading: false,
              sceneIndex: null,
              distance: Infinity
            });
          }
          console.log(`Grid LOD Manager: ${this.sectors.size} sectors initialized`);
        },
        
        update() {
          if (!this.camera || this.sectors.size === 0) return;
          
          const now = performance.now();
          if (now - this.lastUpdateTime < this.updateInterval) return;
          this.lastUpdateTime = now;
          
          this.updateSectorDistances();
          this.processQueue();
        },
        
        updateSectorDistances() {
          const camX = this.camera.position.x;
          const camZ = this.camera.position.z;
          
          for (const [id, sector] of this.sectors) {
            const dx = camX - sector.centerX;
            const dz = camZ - sector.centerZ;
            sector.distance = Math.sqrt(dx * dx + dz * dz);
            
            // Determine target LOD based on distance and navmesh status
            let targetLOD = null;
            const isInside = sector.insideNavmesh !== false; // Default to inside if not specified
            
            if (isInside) {
              // Inside navmesh: use all LOD levels
              if (sector.distance < this.lodDistances.high) {
                targetLOD = 'high';
              } else if (sector.distance < this.lodDistances.medium) {
                targetLOD = 'medium';
              } else if (sector.distance < this.lodDistances.low) {
                targetLOD = 'low';
              }
            } else {
              // Outside navmesh: skip high LOD (background areas)
              if (sector.distance < this.lodDistances.medium) {
                targetLOD = 'medium';
              } else if (sector.distance < this.lodDistances.low) {
                targetLOD = 'low';
              }
            }
            
            if (targetLOD !== sector.currentLOD && targetLOD !== sector.targetLOD && !sector.loading) {
              sector.targetLOD = targetLOD;
              this.loadingQueue.push({ sector, priority: sector.distance });
              this.loadingQueue.sort((a, b) => a.priority - b.priority);
            }
          }
        },
        
        async processQueue() {
          // Only process one at a time to avoid library crashes
          if (this.currentLoads >= this.maxConcurrentLoads || this.loadingQueue.length === 0) {
            return;
          }
          
          const item = this.loadingQueue.shift();
          const sector = item.sector;
          
          if (sector.targetLOD === sector.currentLOD || sector.loading) {
            // Skip and try next
            if (this.loadingQueue.length > 0) {
              setTimeout(() => this.processQueue(), 50);
            }
            return;
          }
          
          this.currentLoads++;
          sector.loading = true;
          
          try {
            await this.changeLOD(sector, sector.targetLOD);
            // Wait before loading next to let library stabilize
            await new Promise(resolve => setTimeout(resolve, this.loadDelay));
          } catch (e) {
            console.warn(`Sector ${sector.id} LOD change failed:`, e);
          }
          
          sector.loading = false;
          this.currentLoads--;
          
          // Process next item
          if (this.loadingQueue.length > 0) {
            setTimeout(() => this.processQueue(), 50);
          }
        },
        
        async changeLOD(sector, newLOD) {
          console.log(`Sector ${sector.id}: ${sector.currentLOD} -> ${newLOD}`);
          
          // Unload current if loaded
          // Note: gaussian-splats-3d doesn't have removeSplatScene in all versions
          // For now we just load additively (memory will grow)
          
          // Load new LOD
          if (newLOD !== null) {
            const file = sector.files[newLOD];
            try {
              const response = await fetch(file, { method: 'HEAD' });
              if (response.ok) {
                await this.viewer.addSplatScene(file, {
                  showLoadingUI: false,
                  progressiveLoad: false
                });
                console.log(`Loaded sector ${sector.id} at ${newLOD}`);
              }
            } catch (e) {
              // File doesn't exist, skip silently
            }
          }
          
          sector.currentLOD = newLOD;
        },
        
        getStats() {
          let stats = { high: 0, medium: 0, low: 0, unloaded: 0 };
          for (const [, s] of this.sectors) {
            if (s.currentLOD) stats[s.currentLOD]++;
            else stats.unloaded++;
          }
          return stats;
        }
      };
      
      gridLODManager.initialize();
      gridLODManager.camera = camera;
      
      // Immediately trigger first update to load initial sectors
      gridLODManager.updateSectorDistances();
      gridLODManager.processQueue();
    }
    
    let lastTime = performance.now();
    
    function animate() {
      requestAnimationFrame(animate);
      
      // Update performance monitor for adaptive quality
      performanceMonitor.update();
      
      // Update Grid LOD Manager
      if (gridLODManager) {
        gridLODManager.update();
      }
      
      if (!camera) return;
      
      const now = performance.now();
      const delta = (now - lastTime) / 1000;
      lastTime = now;
      
      // Turntable mode: just update orbit controls
      if (config.mode === 'turntable') {
        if (orbitControls) {
          orbitControls.update();
        }
        
        // Make hotspots face camera (billboard)
        hotspotMeshes.forEach(group => {
          if (group.visible) {
            group.lookAt(camera.position);
          }
        });
        
        // Render gizmo on top of splat (separate pass after splat renders)
        if (transformControls && selectedHotspotIndex !== null && viewer.renderer) {
          const gizmoObject = transformControls.getHelper ? transformControls.getHelper() : transformControls;
          if (gizmoObject && gizmoObject.visible) {
            viewer.renderer.autoClear = false;
            viewer.renderer.clearDepth();
            viewer.renderer.render(gizmoObject, camera);
            viewer.renderer.autoClear = true;
          }
        }
        
        return;
      }
      
      // Walkthrough mode below
      // Update camera rotation (with 180 degree roll to flip the view)
      camera.rotation.set(pitch, yaw, Math.PI, 'YXZ');
      
      // Render hover indicator scene on top (only when needed)
      if (hoverScene && hoverIndicator && hoverIndicator.visible && viewer.renderer) {
        try {
          viewer.renderer.autoClear = false;
          viewer.renderer.clearDepth();
          viewer.renderer.render(hoverScene, camera);
          viewer.renderer.autoClear = true;
        } catch (e) {
          // Ignore render errors during loading
        }
      }
      
      // Render gizmo on top of splat (separate pass after splat renders) for walkthrough mode
      if (transformControls && selectedHotspotIndex !== null && viewer.renderer) {
        const gizmoObject = transformControls.getHelper ? transformControls.getHelper() : transformControls;
        if (gizmoObject && gizmoObject.visible) {
          viewer.renderer.autoClear = false;
          viewer.renderer.clearDepth();
          viewer.renderer.render(gizmoObject, camera);
          viewer.renderer.autoClear = true;
        }
      }
      
      // Make hotspots face camera (billboard) in walkthrough mode
      hotspotMeshes.forEach(group => {
        if (group.visible) {
          group.lookAt(camera.position);
        }
      });
      
      // Block movement until scene is ready
      if (!sceneReady) return;
      
      // WASD movement (relative to camera direction)
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
      
      // Keep movement horizontal
      forward.y = 0;
      forward.normalize();
      right.y = 0;
      right.normalize();
      
      const speed = moveSpeed * delta;
      
      // Calculate new position
      let newPos = camera.position.clone();
      if (keys.w) newPos.add(forward.clone().multiplyScalar(speed));
      if (keys.s) newPos.add(forward.clone().multiplyScalar(-speed));
      if (keys.a) newPos.add(right.clone().multiplyScalar(-speed));
      if (keys.d) newPos.add(right.clone().multiplyScalar(speed));
      
      // In admin mode: unlimited movement including height (no navmesh boundaries)
      if (isAdmin) {
        camera.position.x = newPos.x;
        camera.position.z = newPos.z;
        // Q/E for height in edit mode
        if (keys.q) camera.position.y -= speed;
        if (keys.e) camera.position.y += speed;
      } else {
        // Normal mode: check navmesh bounds with 0.2m margin from edges
        if (isPositionInNavmesh(newPos.x, newPos.z, 0.2)) {
          camera.position.x = newPos.x;
          camera.position.z = newPos.z;
        }
      }
      
      // Handle click-to-walk movement
      if (isMoving) {
        const distance = moveStart.distanceTo(moveEnd);
        const duration = distance / moveSpeed;
        
        moveProgress += delta / Math.max(duration, 0.1);
        
        if (moveProgress >= 1) {
          moveProgress = 1;
          isMoving = false;
        }
        
        // Smooth easing
        const t = moveProgress < 0.5 
          ? 2 * moveProgress * moveProgress 
          : 1 - Math.pow(-2 * moveProgress + 2, 2) / 2;
        
        camera.position.lerpVectors(moveStart, moveEnd, t);
      }
    }
    
    // Admin functionality
    const adminPanel = document.getElementById('admin-panel');
    const loginModal = document.getElementById('login-modal');
    const adminLoginBtn = document.getElementById('admin-login-btn');
    
    // Show admin button only on localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      adminLoginBtn.style.display = 'block';
    }
    const loginBtn = document.getElementById('login-btn');
    const cancelLoginBtn = document.getElementById('cancel-login-btn');
    const editNavmeshBtn = document.getElementById('edit-navmesh-btn');
    const clearNavmeshBtn = document.getElementById('clear-navmesh-btn');
    const saveNavmeshBtn = document.getElementById('save-navmesh-btn');
    const navmeshStatus = document.getElementById('navmesh-status');
    const loginError = document.getElementById('login-error');
    const adminPanelHeader = document.getElementById('admin-panel-header');
    const adminPanelContent = document.getElementById('admin-panel-content');
    const adminCollapseIcon = document.getElementById('admin-collapse-icon');
    let adminPanelCollapsed = false;
    
    // Admin panel collapse toggle
    adminPanelHeader.addEventListener('click', () => {
      adminPanelCollapsed = !adminPanelCollapsed;
      adminPanelContent.style.display = adminPanelCollapsed ? 'none' : 'block';
      adminCollapseIcon.style.transform = adminPanelCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)';
    });
    
    // Polygon editing state
    let polygonMesh = null;
    let polygonPoints = [];
    let pointMarkers = [];
    
    // Show login modal
    adminLoginBtn.addEventListener('click', () => {
      if (isAdmin) {
        // Logout
        isAdmin = false;
        adminPanel.style.display = 'none';
        adminLoginBtn.textContent = 'Admin';
      } else {
        loginModal.style.display = 'flex';
        document.getElementById('username').focus();
      }
    });
    
    // Cancel login
    cancelLoginBtn.addEventListener('click', () => {
      loginModal.style.display = 'none';
      loginError.textContent = '';
    });
    
    // Login
    loginBtn.addEventListener('click', () => {
      const username = document.getElementById('username').value;
      const password = document.getElementById('password').value;
      
      if (username === 'admin' && password === 'admin') {
        isAdmin = true;
        loginModal.style.display = 'none';
        adminPanel.style.display = 'block';
        adminLoginBtn.textContent = 'Uitloggen';
        loginError.textContent = '';
        document.getElementById('username').value = '';
        document.getElementById('password').value = '';
      } else {
        loginError.textContent = 'Ongeldige inloggegevens';
      }
    });
    
    // Enter key for login
    document.getElementById('password').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') loginBtn.click();
    });
    
    // Make admin panel draggable
    let isDraggingPanel = false;
    let panelOffsetX = 0;
    let panelOffsetY = 0;
    
    const adminPanelDragHandle = adminPanel.querySelector('h3');
    if (adminPanelDragHandle) {
      adminPanelDragHandle.addEventListener('mousedown', (e) => {
        isDraggingPanel = true;
        const rect = adminPanel.getBoundingClientRect();
        panelOffsetX = e.clientX - rect.left;
        panelOffsetY = e.clientY - rect.top;
        adminPanel.style.transition = 'none';
      });
      
      document.addEventListener('mousemove', (e) => {
        if (!isDraggingPanel) return;
        
        const x = e.clientX - panelOffsetX;
        const y = e.clientY - panelOffsetY;
        
        // Keep panel within viewport
        const maxX = window.innerWidth - adminPanel.offsetWidth;
        const maxY = window.innerHeight - adminPanel.offsetHeight;
        
        adminPanel.style.left = Math.max(0, Math.min(x, maxX)) + 'px';
        adminPanel.style.top = Math.max(60, Math.min(y, maxY)) + 'px'; // 60px for header
        adminPanel.style.right = 'auto';
        adminPanel.style.bottom = 'auto';
      });
      
      document.addEventListener('mouseup', () => {
        if (isDraggingPanel) {
          isDraggingPanel = false;
          adminPanel.style.transition = 'all 0.3s';
        }
      });
    }
    
    // Remember collapsed state of admin sections
    const adminSections = adminPanel.querySelectorAll('.admin-section');
    adminSections.forEach((section, i) => {
      const key = `admin_section_${i}_collapsed`;
      // Restore collapsed state
      if (localStorage.getItem(key) === 'true') {
        section.classList.add('collapsed');
      }
      // Save collapsed state on click
      section.querySelector('.admin-section-header').addEventListener('click', () => {
        setTimeout(() => {
          localStorage.setItem(key, section.classList.contains('collapsed'));
        }, 10);
      });
    });
    
    // Edit navmesh toggle
    editNavmeshBtn.addEventListener('click', () => {
      isEditingNavmesh = !isEditingNavmesh;
      editNavmeshBtn.classList.toggle('active', isEditingNavmesh);
      editNavmeshBtn.textContent = isEditingNavmesh ? 'Stop Bewerken' : 'Navmesh Bewerken';
      navmeshStatus.textContent = isEditingNavmesh ? 'Klik om punten toe te voegen' : '';
      
      if (!isEditingNavmesh && polygonPoints.length >= 3) {
        updatePolygonMesh();
      }
    });
    
    // Clear navmesh
    clearNavmeshBtn.addEventListener('click', () => {
      polygonPoints = [];
      navmeshPoints = [];
      clearPolygonVisuals();
      navmeshStatus.textContent = 'Navmesh gewist';
    });
    
    // Save navmesh - download full config JSON
    saveNavmeshBtn.addEventListener('click', () => {
      if (polygonPoints.length < 3) {
        navmeshStatus.textContent = 'Minimaal 3 punten nodig';
        return;
      }
      
      // Prepare polygon data
      const polygonData = polygonPoints.map(p => [p.x, p.z]);
      
      // Update config with new navmesh polygon
      config.navigation.navmeshPolygon = polygonData;
      
      // Create full config JSON
      const jsonOutput = JSON.stringify(config, null, 2);
      
      // Get filename from configUrl
      const filename = configUrl.split('/').pop() || 'config.json';
      
      // Download as JSON file
      const blob = new Blob([jsonOutput], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      navmeshStatus.textContent = 'JSON gedownload!';
    });
    
    function clearPolygonVisuals() {
      // Remove point markers
      pointMarkers.forEach(marker => {
        viewer.threeScene.remove(marker);
      });
      pointMarkers = [];
      
      // Remove polygon mesh
      if (polygonMesh) {
        viewer.threeScene.remove(polygonMesh);
        polygonMesh = null;
      }
    }
    
    function addPolygonPoint(point) {
      polygonPoints.push(point);
      
      // Add visual marker
      const markerGeom = new THREE.SphereGeometry(0.1);
      const markerMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
      const marker = new THREE.Mesh(markerGeom, markerMat);
      marker.position.copy(point);
      viewer.threeScene.add(marker);
      pointMarkers.push(marker);
      
      // Update polygon if we have enough points
      if (polygonPoints.length >= 3) {
        updatePolygonMesh();
      }
      
      navmeshStatus.textContent = `${polygonPoints.length} punten`;
    }
    
    function updatePolygonMesh() {
      // Remove old polygon
      if (polygonMesh) {
        viewer.splatMesh.remove(polygonMesh);
      }
      
      if (polygonPoints.length < 3) return;
      
      // Create shape from points
      const shape = new THREE.Shape();
      shape.moveTo(polygonPoints[0].x, polygonPoints[0].z);
      for (let i = 1; i < polygonPoints.length; i++) {
        shape.lineTo(polygonPoints[i].x, polygonPoints[i].z);
      }
      shape.closePath();
      
      // Create mesh
      const geometry = new THREE.ShapeGeometry(shape);
      const material = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.4,
        side: THREE.DoubleSide
      });
      polygonMesh = new THREE.Mesh(geometry, material);
      polygonMesh.rotation.x = -Math.PI / 2;
      polygonMesh.position.y = config.navigation.navmeshHeight + 0.02;
      viewer.threeScene.add(polygonMesh);
      
      // Update navmesh for click detection
      navMesh = polygonMesh;
    }
    
    // Handle admin click to add polygon points
    let adminMouseDownPos = { x: 0, y: 0 };
    let adminMouseDownTime = 0;
    
    document.addEventListener('mousedown', (e) => {
      if (isAdmin && isEditingNavmesh) {
        adminMouseDownPos = { x: e.clientX, y: e.clientY };
        adminMouseDownTime = Date.now();
      }
    });
    
    document.addEventListener('mouseup', (e) => {
      if (!isAdmin || !isEditingNavmesh || !camera) return;
      
      // Ignore clicks on UI elements
      if (e.target.closest('#admin-panel') || e.target.closest('#header') || e.target.closest('#info-panel')) {
        return;
      }
      
      // Check if this was a click (not a drag)
      const deltaX = Math.abs(e.clientX - adminMouseDownPos.x);
      const deltaY = Math.abs(e.clientY - adminMouseDownPos.y);
      const clickDuration = Date.now() - adminMouseDownTime;
      
      // Only add point if barely moved and short click
      if (deltaX > 5 || deltaY > 5 || clickDuration > 300) {
        return; // Was a drag, not a click
      }
      
      const mouse = new THREE.Vector2();
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
      
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      
      // Intersect with floor plane at navmesh height
      const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -config.navigation.navmeshHeight);
      const point = new THREE.Vector3();
      
      if (raycaster.ray.intersectPlane(floorPlane, point)) {
        addPolygonPoint(point);
      }
    });
    
    // Load saved navmesh from localStorage
    const savedNavmesh = localStorage.getItem(`navmesh_${splatUrl}`);
    if (savedNavmesh) {
      try {
        const points = JSON.parse(savedNavmesh);
        polygonPoints = points.map(p => new THREE.Vector3(p[0], config.navigation.navmeshHeight, p[1]));
        if (polygonPoints.length >= 3) {
          updatePolygonMesh();
        }
      } catch (e) {
        console.log('Could not load saved navmesh');
      }
    }
    
    // ==================== HOTSPOT SYSTEM ====================
    
    // Hotspot state
    let hotspots = [];
    let hotspotMeshes = []; // 3D mesh objects for hotspots
    let isAddingHotspot = false;
    let activePopup = null;
    let hotspotsVisible = true;
    const hotspotsContainer = document.getElementById('hotspots-container');
    const hotspotEditModal = document.getElementById('hotspot-edit-modal');
    const hotspotStatus = document.getElementById('hotspot-status');
    const hotspotsToggleBtn = document.getElementById('hotspots-toggle-btn');
    let pendingHotspotPosition = null;
    
    // Transform controls for hotspot editing (3D gizmo)
    let transformControls = null;
    let selectedHotspotIndex = null;
    
    function setupTransformControls() {
      if (!viewer.renderer || !camera) return;
      
      transformControls = new TransformControls(camera, viewer.renderer.domElement);
      transformControls.setMode('translate');
      transformControls.setSize(0.8);
      
      // In Three.js 0.160.0, TransformControls has a gizmo that needs to be added
      // The gizmo is accessed via the internal _gizmo property or we add the controls directly
      let gizmoObject = transformControls.getHelper ? transformControls.getHelper() : transformControls;
      
      // Set render order high so it renders on top of splat
      gizmoObject.renderOrder = 999999;
      
      // Disable depth test on all gizmo materials so it's always visible
      gizmoObject.traverse((child) => {
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.depthTest = false;
              mat.depthWrite = false;
              mat.transparent = true;
            });
          } else {
            child.material.depthTest = false;
            child.material.depthWrite = false;
            child.material.transparent = true;
          }
        }
        child.renderOrder = 999999;
      });
      
      viewer.threeScene.add(gizmoObject);
      
      // Update hotspot position and scale when dragging
      transformControls.addEventListener('change', () => {
        if (selectedHotspotIndex !== null && hotspotMeshes[selectedHotspotIndex]) {
          const mesh = hotspotMeshes[selectedHotspotIndex];
          const pos = mesh.position;
          hotspots[selectedHotspotIndex].position.copy(pos);
          
          // Calculate scale from mesh scale (base scale is 0.25)
          const scale = mesh.scale.x / 0.25;
          hotspots[selectedHotspotIndex].scale = scale;
          
          // Update editor fields if open
          if (hotspotEditModal.style.display === 'flex') {
            document.getElementById('hotspot-pos-x').value = pos.x.toFixed(2);
            document.getElementById('hotspot-pos-y').value = pos.y.toFixed(2);
            document.getElementById('hotspot-pos-z').value = pos.z.toFixed(2);
            document.getElementById('hotspot-scale').value = Math.min(3, Math.max(0.5, scale)).toFixed(1);
            document.getElementById('hotspot-scale-value').textContent = scale.toFixed(1) + 'x';
          }
        }
      });
      
      // Disable orbit controls and camera look while dragging gizmo
      let isDraggingGizmo = false;
      transformControls.addEventListener('dragging-changed', (event) => {
        isDraggingGizmo = event.value;
        if (orbitControls) {
          orbitControls.enabled = !event.value;
        }
        // Stop camera look when starting to drag gizmo
        if (event.value) {
          isLooking = false;
        }
      });
      
      // Keyboard shortcuts for gizmo
      window.addEventListener('keydown', (e) => {
        if (!transformControls || selectedHotspotIndex === null) return;
        
        if (e.key.toLowerCase() === 'escape') {
          detachGizmo();
          hotspotEditModal.style.transform = 'translateY(-100%)';
          setTimeout(() => hotspotEditModal.style.display = 'none', 300);
        }
      });
      
      // Disable scale and rotate - only translate allowed
      transformControls.showX = true;
      transformControls.showY = true;
      transformControls.showZ = true;
    }
    
    function attachGizmoToHotspot(index) {
      if (!transformControls || !isAdmin) return;
      
      if (index !== null && hotspotMeshes[index]) {
        // Hide the previous selected hotspot icon if any
        if (selectedHotspotIndex !== null && hotspotMeshes[selectedHotspotIndex]) {
          hotspotMeshes[selectedHotspotIndex].visible = true;
        }
        
        selectedHotspotIndex = index;
        // Keep hotspot icon visible during gizmo mode
        transformControls.attach(hotspotMeshes[index]);
        hotspotStatus.textContent = `Hotspot ${index + 1} - G=verplaatsen, S=schalen, Esc=sluiten`;
      }
    }
    
    function detachGizmo() {
      if (transformControls) {
        // Show the hotspot icon again
        if (selectedHotspotIndex !== null && hotspotMeshes[selectedHotspotIndex]) {
          hotspotMeshes[selectedHotspotIndex].visible = true;
        }
        transformControls.detach();
        selectedHotspotIndex = null;
      }
    }
    
    // Create info icon texture using canvas with transparent background
    function createInfoIconTexture() {
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      const ctx = canvas.getContext('2d');
      
      // Clear canvas (transparent background)
      ctx.clearRect(0, 0, 128, 128);
      
      // Draw circle background (turquoise) - no border, like the ? button
      ctx.beginPath();
      ctx.arc(64, 64, 60, 0, Math.PI * 2);
      ctx.fillStyle = '#00C4C4';
      ctx.fill();
      
      // Draw info "i" manually for clean look
      ctx.fillStyle = '#000000';
      
      // Draw the dot
      ctx.beginPath();
      ctx.arc(64, 30, 8, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw the stem with rounded ends
      ctx.beginPath();
      ctx.roundRect(56, 44, 16, 46, 4);
      ctx.fill();
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    }
    
    const infoIconTexture = createInfoIconTexture();
    
    // Load hotspots from config
    if (config.hotspots && Array.isArray(config.hotspots)) {
      hotspots = config.hotspots.map(h => ({
        position: new THREE.Vector3(h.position[0], h.position[1], h.position[2]),
        title: h.title,
        description: h.description,
        scale: h.scale || 1.0
      }));
    }
    
    // Create 3D hotspots function - called after viewer is ready
    function create3DHotspots() {
      if (!viewer || !viewer.threeScene || hotspots.length === 0) return;
      
      console.log('Creating 3D hotspots, count:', hotspots.length);
      
      hotspots.forEach((hotspot, index) => {
        const scale = hotspot.scale || 1.0;
        
        // Create sprite with info icon texture
        const spriteMaterial = new THREE.SpriteMaterial({ 
          map: infoIconTexture,
          transparent: true,
          alphaTest: 0.1,
          sizeAttenuation: true
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.set(hotspot.position.x, hotspot.position.y, hotspot.position.z);
        sprite.scale.set(0.25 * scale, 0.25 * scale, 1);
        sprite.userData = { hotspotIndex: index, isHotspot: true };
        
        viewer.threeScene.add(sprite);
        hotspotMeshes.push(sprite);
        
        console.log('Created hotspot at:', sprite.position);
      });
    }
    
    
    // UI toggle button - toggles HQ, hotspots, and navigation panel
    const uiToggleBtn = document.getElementById('ui-toggle-btn');
    const infoPanel = document.getElementById('info-panel');
    const hqBtn = document.getElementById('hq-btn');
    let uiVisible = true;
    
    if (uiToggleBtn) {
      uiToggleBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        uiVisible = !uiVisible;
        
        // Show/hide HQ button
        if (hqBtn) {
          hqBtn.style.display = uiVisible ? 'flex' : 'none';
        }
        
        // Show/hide navigation panel
        if (infoPanel) {
          infoPanel.style.display = uiVisible ? 'block' : 'none';
        }
        
        // Show/hide 3D hotspot meshes
        hotspotsVisible = uiVisible;
        hotspotMeshes.forEach(mesh => {
          mesh.visible = uiVisible;
        });
        
        uiToggleBtn.style.background = uiVisible ? 'var(--twinity-turquoise)' : 'rgba(18, 18, 18, 0.9)';
        uiToggleBtn.style.color = uiVisible ? 'var(--twinity-black)' : 'white';
        uiToggleBtn.style.borderColor = uiVisible ? 'var(--twinity-turquoise)' : 'rgba(0, 196, 196, 0.3)';
      });
    }
    
    // Hotspot list element
    const hotspotList = document.getElementById('hotspot-list');
    let editingHotspotIndex = null;
    
    // Render hotspot list in admin panel
    function renderHotspotList() {
      if (!hotspotList) return;
      
      hotspotList.innerHTML = '';
      if (hotspots.length === 0) {
        hotspotList.innerHTML = '<div style="color: rgba(255,255,255,0.5); font-size: 12px; padding: 8px;">Geen hotspots</div>';
        return;
      }
      hotspots.forEach((h, i) => {
        const item = document.createElement('div');
        item.style.cssText = 'display: flex; justify-content: space-between; align-items: center; padding: 8px; margin-bottom: 4px; background: rgba(255,255,255,0.1); border-radius: 4px;';
        const title = h.title || `Hotspot ${i + 1}`;
        item.innerHTML = `
          <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px;"><strong style="color: var(--twinity-turquoise);">${i + 1}.</strong> ${title}</span>
          <button class="edit-hotspot" data-index="${i}" style="padding: 4px 8px; margin-left: 4px; background: var(--twinity-turquoise); color: black; border: none; cursor: pointer; border-radius: 4px;">✎</button>
          <button class="delete-hotspot" data-index="${i}" style="padding: 4px 8px; margin-left: 4px; background: #ff4444; color: white; border: none; cursor: pointer; border-radius: 4px;">✕</button>
        `;
        hotspotList.appendChild(item);
      });
      console.log('Rendered hotspot list:', hotspots.length, 'items');
      
      // Add event listeners
      hotspotList.querySelectorAll('.edit-hotspot').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const index = parseInt(e.target.dataset.index);
          openHotspotEditor(index);
        });
      });
      
      hotspotList.querySelectorAll('.delete-hotspot').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const index = parseInt(e.target.dataset.index);
          deleteHotspot(index);
        });
      });
    }
    
    // Open hotspot editor for existing hotspot
    function openHotspotEditor(index) {
      editingHotspotIndex = index;
      const hotspot = hotspots[index];
      document.getElementById('hotspot-title').value = hotspot.title || '';
      document.getElementById('hotspot-description').value = hotspot.description || '';
      document.getElementById('hotspot-pos-x').value = hotspot.position.x.toFixed(2);
      document.getElementById('hotspot-pos-y').value = hotspot.position.y.toFixed(2);
      document.getElementById('hotspot-pos-z').value = hotspot.position.z.toFixed(2);
      document.getElementById('hotspot-scale').value = hotspot.scale || 1;
      document.getElementById('hotspot-scale-value').textContent = (hotspot.scale || 1).toFixed(1) + 'x';
      hotspotEditModal.style.display = 'block';
      setTimeout(() => hotspotEditModal.style.transform = 'translateY(0)', 10);
      document.getElementById('hotspot-title').focus();
    }
    
    // Live update function for hotspot position and scale
    function updateHotspotLive() {
      if (editingHotspotIndex === null) return;
      
      const posX = parseFloat(document.getElementById('hotspot-pos-x').value) || 0;
      const posY = parseFloat(document.getElementById('hotspot-pos-y').value) || 0;
      const posZ = parseFloat(document.getElementById('hotspot-pos-z').value) || 0;
      const scale = parseFloat(document.getElementById('hotspot-scale').value) || 1;
      
      // Update 3D mesh position and scale live
      if (hotspotMeshes[editingHotspotIndex]) {
        hotspotMeshes[editingHotspotIndex].position.set(posX, posY, posZ);
        hotspotMeshes[editingHotspotIndex].scale.set(0.25 * scale, 0.25 * scale, 1);
      }
    }
    
    // Scale slider live update
    document.getElementById('hotspot-scale').addEventListener('input', (e) => {
      document.getElementById('hotspot-scale-value').textContent = parseFloat(e.target.value).toFixed(1) + 'x';
      updateHotspotLive();
    });
    
    // XYZ position live update
    document.getElementById('hotspot-pos-x').addEventListener('input', updateHotspotLive);
    document.getElementById('hotspot-pos-y').addEventListener('input', updateHotspotLive);
    document.getElementById('hotspot-pos-z').addEventListener('input', updateHotspotLive);
    
    // Delete hotspot
    function deleteHotspot(index) {
      // Remove 3D mesh
      if (hotspotMeshes[index]) {
        viewer.threeScene.remove(hotspotMeshes[index]);
        hotspotMeshes.splice(index, 1);
      }
      // Remove from array
      hotspots.splice(index, 1);
      // Update mesh indices
      hotspotMeshes.forEach((mesh, i) => {
        mesh.userData.hotspotIndex = i;
      });
      renderHotspotList();
      hotspotStatus.textContent = 'Hotspot verwijderd';
    }
    
    // Add hotspot button
    document.getElementById('add-hotspot-btn').addEventListener('click', () => {
      isAddingHotspot = !isAddingHotspot;
      editingHotspotIndex = null; // New hotspot, not editing
      document.getElementById('add-hotspot-btn').textContent = isAddingHotspot ? 'Klik in scene...' : 'Toevoegen';
      document.getElementById('add-hotspot-btn').classList.toggle('active', isAddingHotspot);
      if (isAddingHotspot) {
        hotspotStatus.textContent = 'Klik in de 3D scene om hotspot te plaatsen';
      } else {
        hotspotStatus.textContent = '';
      }
    });
    
    // Save hotspots button (export full config)
    document.getElementById('save-hotspots-btn').addEventListener('click', async () => {
      // Update hotspots in config
      const hotspotsData = hotspots.map(h => ({
        position: [h.position.x, h.position.y, h.position.z],
        title: h.title,
        description: h.description,
        scale: h.scale || 1.0
      }));
      
      // Create full config with updated hotspots
      const fullConfig = { ...config };
      fullConfig.hotspots = hotspotsData;
      
      const jsonOutput = JSON.stringify(fullConfig, null, 2);
      
      // Get filename from URL
      const configFileName = configUrl.split('/').pop().replace('.json', '') + '.json';
      
      // Copy to clipboard
      navigator.clipboard.writeText(jsonOutput).then(() => {
        hotspotStatus.textContent = 'Config gekopieerd + download gestart';
      }).catch(() => {
        hotspotStatus.textContent = 'Download gestart';
      });
      
      // Save to localStorage
      localStorage.setItem(`hotspots_${splatUrl}`, JSON.stringify(hotspotsData));
      
      // Download full config JSON file
      const blob = new Blob([jsonOutput], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = configFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('Full config exported:', configFileName);
    });
    
    // Hotspot edit modal handlers
    document.getElementById('save-hotspot-edit-btn').addEventListener('click', () => {
      const title = document.getElementById('hotspot-title').value.trim();
      const description = document.getElementById('hotspot-description').value.trim();
      const posX = parseFloat(document.getElementById('hotspot-pos-x').value) || 0;
      const posY = parseFloat(document.getElementById('hotspot-pos-y').value) || 0;
      const posZ = parseFloat(document.getElementById('hotspot-pos-z').value) || 0;
      const scale = parseFloat(document.getElementById('hotspot-scale').value) || 1;
      
      console.log('Saving hotspot:', { title, posX, posY, posZ, scale, editingHotspotIndex });
      
      if (!title) {
        alert('Vul een titel in');
        return;
      }
      
      if (editingHotspotIndex !== null) {
        // Editing existing hotspot
        hotspots[editingHotspotIndex].title = title;
        hotspots[editingHotspotIndex].description = description;
        hotspots[editingHotspotIndex].position.set(posX, posY, posZ);
        hotspots[editingHotspotIndex].scale = scale;
        
        // Update 3D mesh position and scale
        if (hotspotMeshes[editingHotspotIndex]) {
          hotspotMeshes[editingHotspotIndex].position.set(posX, posY, posZ);
          hotspotMeshes[editingHotspotIndex].scale.set(0.25 * scale, 0.25 * scale, 1);
        }
        
        hotspotStatus.textContent = 'Hotspot bijgewerkt';
      } else {
        // Adding new hotspot
        const position = pendingHotspotPosition || new THREE.Vector3(posX, posY, posZ);
        const newHotspot = {
          position: position,
          title: title,
          description: description,
          scale: scale
        };
        hotspots.push(newHotspot);
        
        // Create 3D sprite for new hotspot
        const index = hotspots.length - 1;
        const spriteMaterial = new THREE.SpriteMaterial({ 
          map: infoIconTexture,
          transparent: true,
          alphaTest: 0.1,
          sizeAttenuation: true
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(0.25 * scale, 0.25 * scale, 1);
        sprite.userData = { hotspotIndex: index, isHotspot: true };
        viewer.threeScene.add(sprite);
        hotspotMeshes.push(sprite);
        
        hotspotStatus.textContent = `${hotspots.length} hotspot(s)`;
      }
      
      hotspotEditModal.style.transform = 'translateY(-100%)';
      setTimeout(() => hotspotEditModal.style.display = 'none', 300);
      document.getElementById('hotspot-title').value = '';
      document.getElementById('hotspot-description').value = '';
      pendingHotspotPosition = null;
      editingHotspotIndex = null;
      isAddingHotspot = false;
      document.getElementById('add-hotspot-btn').textContent = 'Toevoegen';
      document.getElementById('add-hotspot-btn').classList.remove('active');
      
      renderHotspotList();
      detachGizmo();
    });
    
    document.getElementById('cancel-hotspot-edit-btn').addEventListener('click', () => {
      hotspotEditModal.style.transform = 'translateY(-100%)';
      setTimeout(() => hotspotEditModal.style.display = 'none', 300);
      document.getElementById('hotspot-title').value = '';
      document.getElementById('hotspot-description').value = '';
      pendingHotspotPosition = null;
      editingHotspotIndex = null;
      detachGizmo();
    });
    
    // Collapse hotspot modal button - minimize instead of close
    const hotspotModalContent = document.getElementById('hotspot-modal-content');
    const hotspotModalMinimized = document.getElementById('hotspot-modal-minimized');
    
    document.getElementById('collapse-hotspot-modal-btn').addEventListener('click', () => {
      // Minimize: hide content, show minimized bar
      hotspotModalContent.style.display = 'none';
      hotspotModalMinimized.style.display = 'block';
    });
    
    // Expand hotspot modal when clicking minimized bar
    hotspotModalMinimized.addEventListener('click', () => {
      hotspotModalContent.style.display = 'block';
      hotspotModalMinimized.style.display = 'none';
    });
    
    // Initial render of hotspot list
    renderHotspotList();
    
    // Click handler for adding hotspots
    document.addEventListener('click', (e) => {
      if (!isAddingHotspot || !camera) return;
      
      // Ignore clicks on UI elements
      if (e.target.closest('#admin-panel') || e.target.closest('#header') || 
          e.target.closest('#info-panel') || e.target.closest('#hotspot-edit-modal') ||
          e.target.closest('.hotspot') || e.target.closest('.hotspot-popup')) {
        return;
      }
      
      const mouse = new THREE.Vector2();
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
      
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      
      // For turntable: intersect with a sphere around the target
      // For walkthrough: intersect with floor plane
      let point = new THREE.Vector3();
      
      if (config.mode === 'turntable') {
        // Use a plane facing the camera at a reasonable distance
        const cameraDir = new THREE.Vector3();
        camera.getWorldDirection(cameraDir);
        const planeNormal = cameraDir.clone().negate();
        const planePoint = camera.position.clone().add(cameraDir.multiplyScalar(config.turntable.startDistance || 3));
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, planePoint);
        raycaster.ray.intersectPlane(plane, point);
      } else {
        // Walkthrough: use floor plane
        const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -config.navigation.navmeshHeight);
        raycaster.ray.intersectPlane(floorPlane, point);
        // Raise hotspot to eye level
        point.y = config.navigation.floorHeight + config.navigation.eyeHeightStanding * 0.7;
      }
      
      if (point) {
        console.log('Adding hotspot at:', point);
        pendingHotspotPosition = point.clone();
        
        // Fill in XYZ fields with the clicked position
        document.getElementById('hotspot-pos-x').value = point.x.toFixed(2);
        document.getElementById('hotspot-pos-y').value = point.y.toFixed(2);
        document.getElementById('hotspot-pos-z').value = point.z.toFixed(2);
        document.getElementById('hotspot-scale').value = 1;
        document.getElementById('hotspot-scale-value').textContent = '1.0x';
        document.getElementById('hotspot-title').value = '';
        document.getElementById('hotspot-description').value = '';
        
        hotspotEditModal.style.display = 'block';
        setTimeout(() => hotspotEditModal.style.transform = 'translateY(0)', 10);
        document.getElementById('hotspot-title').focus();
      }
    });
    
    // Check if a 3D hotspot was clicked
    function check3DHotspotClick(e) {
      if (!camera || !hotspotsVisible || hotspotMeshes.length === 0) return false;
      
      const mouse = new THREE.Vector2();
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
      
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      
      // Check intersection with hotspot meshes (recursive for groups)
      const intersects = raycaster.intersectObjects(hotspotMeshes, true);
      
      if (intersects.length > 0) {
        // Find the parent group with hotspotIndex
        let obj = intersects[0].object;
        while (obj && obj.userData.hotspotIndex === undefined) {
          obj = obj.parent;
        }
        
        if (obj && obj.userData.hotspotIndex !== undefined) {
          // In admin mode, attach gizmo and open editor
          if (isAdmin) {
            attachGizmoToHotspot(obj.userData.hotspotIndex);
            openHotspotEditor(obj.userData.hotspotIndex);
          } else {
            showHotspotPopup(obj.userData.hotspotIndex);
          }
          return true;
        }
      }
      return false;
    }
    
    // Show hotspot popup
    function showHotspotPopup(index) {
      // Close existing popup
      if (activePopup) {
        activePopup.remove();
        activePopup = null;
      }
      
      const hotspot = hotspots[index];
      if (!hotspot) return;
      
      const popup = document.createElement('div');
      popup.className = 'hotspot-popup';
      popup.style.position = 'fixed';
      popup.style.top = '50%';
      popup.style.left = '50%';
      popup.style.transform = 'translate(-50%, -50%)';
      popup.innerHTML = `
        <button class="hotspot-popup-close">&times;</button>
        <h4>${hotspot.title}</h4>
        <p>${hotspot.description}</p>
      `;
      
      popup.querySelector('.hotspot-popup-close').addEventListener('click', () => {
        popup.remove();
        activePopup = null;
      });
      
      hotspotsContainer.appendChild(popup);
      activePopup = popup;
    }
    
    // Render hotspots - now only used for admin list, 3D sprites handle visual
    function renderHotspots() {
      // Clear any old 2D overlays - we use 3D sprites now
      hotspotsContainer.innerHTML = '';
    }
    
    // Toggle hotspot popup
    function toggleHotspotPopup(index, hotspotEl) {
      // Close existing popup
      if (activePopup) {
        activePopup.remove();
        if (activePopup.dataset.index === String(index)) {
          activePopup = null;
          return;
        }
      }
      
      const hotspot = hotspots[index];
      const popup = document.createElement('div');
      popup.className = 'hotspot-popup';
      popup.dataset.index = index;
      popup.innerHTML = `
        <button class="hotspot-popup-close">&times;</button>
        <h4>${hotspot.title}</h4>
        <p>${hotspot.description}</p>
      `;
      
      popup.querySelector('.hotspot-popup-close').addEventListener('click', (e) => {
        e.stopPropagation();
        popup.remove();
        activePopup = null;
      });
      
      hotspotsContainer.appendChild(popup);
      activePopup = popup;
      
      // Position popup above hotspot
      updatePopupPosition(popup, hotspotEl);
    }
    
    function updatePopupPosition(popup, hotspotEl) {
      const rect = hotspotEl.getBoundingClientRect();
      popup.style.left = rect.left + rect.width / 2 + 'px';
      popup.style.top = rect.top + 'px';
    }
    
    // Update hotspot screen positions in animation loop
    function updateHotspotPositions() {
      if (!camera || hotspots.length === 0) return;
      
      const hotspotEls = hotspotsContainer.querySelectorAll('.hotspot');
      
      hotspots.forEach((hotspot, index) => {
        const el = hotspotEls[index];
        if (!el) return;
        
        // Project 3D position to screen
        const screenPos = hotspot.position.clone().project(camera);
        
        // Check if in front of camera
        const cameraDir = new THREE.Vector3();
        camera.getWorldDirection(cameraDir);
        const toHotspot = hotspot.position.clone().sub(camera.position);
        const isBehind = toHotspot.dot(cameraDir) < 0;
        
        if (isBehind || screenPos.z > 1) {
          el.style.display = 'none';
          return;
        }
        
        el.style.display = 'flex';
        
        const x = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;
        
        el.style.left = x + 'px';
        el.style.top = y + 'px';
        
        // Update popup position if open
        if (activePopup && activePopup.dataset.index === String(index)) {
          updatePopupPosition(activePopup, el);
        }
      });
    }
    
    // Add hotspot position update to animation loop
    const originalAnimate = animate;
    function animateWithHotspots() {
      originalAnimate();
      updateHotspotPositions();
    }
    
    // Override requestAnimationFrame call
    // Actually, let's just call updateHotspotPositions in the existing animate
    
    // Load hotspots from localStorage
    const savedHotspots = localStorage.getItem(`hotspots_${splatUrl}`);
    if (savedHotspots && (!config.hotspots || config.hotspots.length === 0)) {
      try {
        const hotspotsData = JSON.parse(savedHotspots);
        hotspots = hotspotsData.map(h => ({
          position: new THREE.Vector3(h.position[0], h.position[1], h.position[2]),
          title: h.title,
          description: h.description
        }));
        renderHotspots();
      } catch (e) {
        console.log('Could not load saved hotspots');
      }
    }
    
    // Update hotspots at lower framerate on mobile for better performance
    const hotspotUpdateInterval = isMobileDevice() ? 50 : 16;
    setInterval(updateHotspotPositions, hotspotUpdateInterval);
    
      